/**
 * Hook para sincronização de usuário com Firestore
 * Garante um único documento por membro (consolida duplicados por email)
 */

import { useState, useCallback } from "react";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  collection,
  query,
  where,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../config/firebase";
import { determineUserRole, UserRole, ROLE_HIERARCHY } from "../types/church";
import { MembrosService } from "../services/membrosService.js";

const usersRef = collection(db, "users");
const DEV_EMAIL = import.meta.env.VITE_DESENVOLVEDOR_EMAIL;

/**
 * Mapear tipo de membro para role do sistema
 * Os valores devem corresponder exatamente aos de UserRole/ROLE_HIERARCHY
 */
const mapearTipoParaRole = (tipo) => {
  const tipoUpper = (tipo || "").toUpperCase();
  const mapeamento = {
    DESENVOLVEDOR: UserRole.DESENVOLVEDOR,
    PASTOR: UserRole.PASTOR,
    PRESBITERO: UserRole.PRESBITERO,
    SECRETARIO: UserRole.SECRETARIO,
    SEGUNDO_SECRETARIO: UserRole.SEGUNDO_SECRETARIO,
    TESOUREIRO: UserRole.TESOUREIRO,
    SEGUNDO_TESOUREIRO: UserRole.SEGUNDO_TESOUREIRO,
    DIRETOR_PATRIMONIO: UserRole.DIRETOR_PATRIMONIO,
    DIACONO: UserRole.DIACONO,
    MEMBRO: UserRole.MEMBRO,
    CONGREGADO: UserRole.MEMBRO,
    FREQUENTADOR: UserRole.MEMBRO,
  };
  return mapeamento[tipoUpper] || UserRole.MEMBRO;
};

/**
 * Hook para sincronização de dados do usuário
 */
export function useUserSync() {
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);

  /**
   * Busca documento duplicado na collection users pelo email
   * (documentos com o mesmo email mas ID diferente do UID atual)
   */
  const findDuplicateByEmail = useCallback(async (email, currentUid) => {
    try {
      const q = query(usersRef, where("email", "==", email));
      const snapshot = await getDocs(q);

      for (const docSnap of snapshot.docs) {
        if (docSnap.id !== currentUid) {
          return { id: docSnap.id, data: docSnap.data() };
        }
      }
    } catch (error) {
      console.warn("[useUserSync] Erro ao buscar duplicado por email:", error);
    }
    return null;
  }, []);

  /**
   * Criar ou atualizar dados do usuário no Firestore
   * SEMPRE valida email contra coleção "membros" em todo login
   * Se existir duplicado por email, consolida no documento do UID atual
   */
  const createOrUpdateChurchUser = useCallback(async (user) => {
    setSyncLoading(true);
    setSyncError(null);

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      // Passo 1: Garantir que existe um doc users para ter permissão de leitura
      let existingData = null;

      if (userDocSnap.exists()) {
        existingData = userDocSnap.data();

        // Consolidar duplicados por email (se houver)
        if (user.email) {
          const duplicate = await findDuplicateByEmail(user.email, user.uid);
          if (duplicate) {
            const dupData = duplicate.data;
            const currentRole = existingData.role || "MEMBRO";
            const dupRole = dupData.role || "MEMBRO";

            // Se o duplicado tem role melhor, mesclar dados
            if (dupRole !== "MEMBRO" && currentRole === "MEMBRO") {
              existingData = {
                ...existingData,
                ...dupData,
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || dupData.displayName || existingData.displayName,
              };
            }

            try {
              await deleteDoc(doc(db, "users", duplicate.id));
              console.log("[useUserSync] Duplicado removido:", duplicate.id);
            } catch (delError) {
              console.warn("[useUserSync] Erro ao remover duplicado:", delError);
            }
          }
        }
      } else {
        // Documento não existe - buscar duplicado por email
        if (user.email) {
          const duplicate = await findDuplicateByEmail(user.email, user.uid);
          if (duplicate) {
            existingData = duplicate.data;
            // Migrar dados do duplicado para o UID atual
            await setDoc(userDocRef, { ...duplicate.data, uid: user.uid, updatedAt: new Date() });
            await deleteDoc(doc(db, "users", duplicate.id));
            console.log("[useUserSync] Usuário migrado de", duplicate.id, "para", user.uid);
          }
        }

        // Sem documento - criar doc temporário para ter permissão de leitura
        if (!existingData) {
          const tempUserData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            role: "MEMBRO",
            nome: user.displayName,
            musico: false,
            _pendente: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          await setDoc(userDocRef, tempUserData);
          console.log("[useUserSync] Doc temporário criado para validação");
        }
      }

      // Passo 2: SEMPRE validar email contra coleção membros (a cada login)
      // Bypass: email do desenvolvedor sempre tem acesso
      const isDesenvolvedor = DEV_EMAIL && user.email?.toLowerCase() === DEV_EMAIL.toLowerCase();

      let verificacao;
      try {
        verificacao = await MembrosService.verificarAcessoPorEmail(user.email);
      } catch (verifError) {
        console.error("[useUserSync] Erro na verificação:", verifError);
        // Desenvolvedor passa mesmo com erro na verificação
        if (isDesenvolvedor) {
          console.log("[useUserSync] Bypass desenvolvedor - erro na verificação ignorado");
          verificacao = { temAcesso: true, membro: null };
        } else {
          if (!existingData || existingData._pendente) {
            await deleteDoc(userDocRef);
          }
          await signOut(auth);
          const err = new Error(
            "Erro ao verificar seu acesso. Tente novamente mais tarde."
          );
          err.code = "auth/email-nao-cadastrado";
          throw err;
        }
      }

      if (!verificacao.temAcesso) {
        // Desenvolvedor sempre entra, mesmo sem estar na coleção membros
        if (isDesenvolvedor) {
          console.log("[useUserSync] Bypass desenvolvedor - acesso concedido");
          verificacao = { temAcesso: true, membro: null };
        } else {
          console.warn("[useUserSync] Acesso negado para:", user.email, "-", verificacao.motivo);
          try {
            await deleteDoc(userDocRef);
          } catch (delErr) {
            console.warn("[useUserSync] Erro ao limpar doc:", delErr);
          }
          await signOut(auth);
          const mensagem =
            verificacao.motivo === "Erro interno do sistema"
              ? "Erro ao verificar seu acesso. Tente novamente mais tarde."
              : verificacao.motivo === "Membro inativo"
                ? "Sua conta de membro está inativa. Fale com a secretaria."
                : "Seu email não está cadastrado como membro da igreja. Fale com a secretaria para receber um convite.";
          const err = new Error(mensagem);
          err.code = "auth/email-nao-cadastrado";
          throw err;
        }
      }

      // Passo 3: Validado - atualizar doc com dados reais do membro
      const membro = verificacao.membro;

      // Determinar a role usando a MAIOR entre todas as fontes:
      // 1. membro.role (coleção membros - atribuída via RoleManagementService)
      // 2. existingData.role (coleção users - role atual do usuário)
      // 3. membro.tipo mapeado (fallback do tipo de membro)
      let role;
      if (isDesenvolvedor) {
        role = UserRole.DESENVOLVEDOR;
      } else {
        const roleValues = Object.values(UserRole);
        const candidates = [];

        // Role da coleção membros
        if (membro?.role && roleValues.includes(membro.role)) {
          candidates.push(membro.role);
        }
        // Role atual da coleção users (preserva alterações manuais)
        if (existingData?.role && roleValues.includes(existingData.role) && !existingData._pendente) {
          candidates.push(existingData.role);
        }
        // Mapeamento do tipo de membro
        if (membro?.tipo) {
          candidates.push(mapearTipoParaRole(membro.tipo));
        }

        // Usar a role com maior hierarquia
        if (candidates.length > 0) {
          role = candidates.reduce((best, current) =>
            (ROLE_HIERARCHY[current] || 0) > (ROLE_HIERARCHY[best] || 0) ? current : best
          );
        } else {
          role = determineUserRole(user.email);
        }
      }

      const churchUserData = {
        ...(existingData || {}),
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role,
        nome: membro?.nome || existingData?.nome || user.displayName,
        membro_id: membro?.id || null,
        musico: existingData?.musico || false,
        _pendente: false,
        createdAt: existingData?.createdAt || new Date(),
        updatedAt: new Date(),
      };

      await setDoc(userDocRef, churchUserData);
      return churchUserData;
    } catch (error) {
      console.error("[useUserSync] Erro ao criar/atualizar usuário:", error);
      setSyncError(error.message);
      throw error;
    } finally {
      setSyncLoading(false);
    }
  }, [findDuplicateByEmail]);

  /**
   * Salvar dados no localStorage
   */
  const salvarDadosLocalStorage = useCallback((user, churchUser) => {
    try {
      const userDataForStorage = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role: churchUser?.role,
      };
      localStorage.setItem("currentUser", JSON.stringify(userDataForStorage));
      localStorage.setItem("churchUser", JSON.stringify(churchUser));
    } catch (error) {
      console.warn("[useUserSync] Erro ao salvar no localStorage:", error);
    }
  }, []);

  const clearError = useCallback(() => {
    setSyncError(null);
  }, []);

  return {
    loading: syncLoading,
    error: syncError,
    createOrUpdateChurchUser,
    salvarDadosLocalStorage,
    clearError,
  };
}

export default useUserSync;
